import {
  Cache,
  UpdateResolver,
  UpdatesConfig,
} from "@urql/exchange-graphcache";

import {
  GetSessionObjects,
  GetSessionObjectsQueryResult,
  GetSessionObjectsQueryVariables,
} from "@/features/forge/forge-session/queries.graphql";

import {
  MutationCreateInferenceArgs,
  SessionObjectEvent,
  SubscriptionOnInferenceProgressArgs,
  SubscriptionOnSessionObjectEventArgs,
} from "../schema.graphql";

type SessionObjectEdgeNode = NonNullable<
  NonNullable<
    NonNullable<
      Extract<
        NonNullable<GetSessionObjectsQueryResult["session"]>,
        { __typename: "Session" }
      >["objects"]["edges"]
    >[number]
  >["node"]
>;

/**
 * Prepend new objects into every cached base-page of GetSessionObjects
 * for a given session. Uses cache.updateQuery so edges are written directly
 * into the connection, which graphcache's relay resolver then picks up.
 *
 * Callers should enrich objects with all fields expected by SessionFile
 * (e.g. the `inference` back-link) to avoid graphcache detecting partial
 * entities and triggering unnecessary background refetches.
 */
const prependSessionObjects = (
  cache: Cache,
  sessionId: string,
  newObjects: Array<{ __typename: string; id: string }>,
) => {
  if (newObjects.length === 0) return;

  const sessionEntity = { __typename: "Session", id: sessionId };
  const sessionEntityKey = cache.keyOfEntity(sessionEntity);
  if (!sessionEntityKey) return;

  // Find all cached variants of Session.objects (different filter/sort combos).
  const fields = cache.inspectFields(sessionEntityKey);
  const objectsFields = fields.filter((f) => f.fieldName === "objects");

  for (const field of objectsFields) {
    const fieldArgs = field.arguments as Record<string, unknown> | null;
    const input = fieldArgs?.input as Record<string, unknown> | undefined;

    // Only update base pages (where after is null/absent).
    if (input?.after) continue;

    // Reconstruct the GetSessionObjects query variables from the
    // Session.objects field arguments. The query passes top-level variables
    // ($after, $first, etc.) which get assembled into objects(input: { ... }).
    // Only include keys present in the cached args so stringifyVariables matches.
    const queryVariables: GetSessionObjectsQueryVariables = {
      getSessionByIdInput: { sessionId },
      ...(input ?? {}),
    };

    cache.updateQuery<
      GetSessionObjectsQueryResult,
      GetSessionObjectsQueryVariables
    >(
      { query: GetSessionObjects, variables: queryVariables },
      (data): GetSessionObjectsQueryResult | null => {
        if (!data?.session || data.session.__typename !== "Session") {
          return data;
        }

        const { session } = data;
        const objects = session.objects;
        if (!objects) return data;

        const existingIds = new Set(
          (objects.edges ?? []).map((edge) => edge?.node?.id),
        );

        const newEdges = newObjects
          .filter((obj) => !existingIds.has(obj.id))
          .map((obj) => ({
            __typename: "SessionObjectEdge" as const,
            cursor: obj.id,
            // Graphcache normalizes by entity key (__typename + id),
            // so the cache resolves all fields even though the TS types
            // differ between fragments.
            node: obj as unknown as SessionObjectEdgeNode,
          }));

        if (newEdges.length === 0) return data;

        return {
          ...data,
          session: {
            ...session,
            objects: {
              ...objects,
              edges: [...newEdges, ...(objects.edges ?? [])],
            },
          },
        };
      },
    );
  }

  // Note: cache.updateQuery writes prepended edges into the normalized
  // store. If the File entities include all fields from SessionFile
  // (including the `inference` back-link), graphcache won't detect
  // partial data and no background refetch is triggered.
};

/**
 * On subscription: when a new session object is created, prepend it.
 */
const onSessionObjectEvent: UpdateResolver = (
  {
    onSessionObjectEvent: event,
  }: {
    onSessionObjectEvent?: SessionObjectEvent | null;
  },
  args: SubscriptionOnSessionObjectEventArgs,
  cache,
) => {
  if (!event || event.type !== "CREATED") return;
  prependSessionObjects(cache, args.input.sessionId, [event.object]);
};

type InferencePayload = {
  __typename: string;
  id: string;
  status: string;
  createdAt: string;
  etaAt?: string | null;
  errorCode?: string | null;
  session?: { id: string } | null;
  files?: Array<{ __typename: string; id: string }>;
};

/**
 * Prepend an inference's files into the session grid cache.
 *
 * The files carry only InferenceFile-fragment fields and lack the `inference`
 * back-link that SessionFile expects. Without it graphcache marks the File
 * entities partial and triggers a background refetch, causing grid flicker as
 * grouping fields go undefined → real. We enrich each file with the back-link so
 * graphcache has everything it needs. Returns early when there are no files, so
 * it's safe to call for in-progress inferences that haven't produced any yet.
 */
const prependInferenceFiles = (cache: Cache, inference: InferencePayload) => {
  if (inference.__typename !== "Inference") return;

  const sessionId = inference.session?.id;
  if (!sessionId) return;

  const files = inference.files ?? [];
  if (files.length === 0) return;

  const inferenceBackLink = {
    __typename: "Inference" as const,
    id: inference.id,
    status: inference.status,
    createdAt: inference.createdAt,
    etaAt: inference.etaAt ?? null,
    errorCode: inference.errorCode ?? null,
  };

  const enrichedFiles = files.map((file) => ({
    ...file,
    inference: inferenceBackLink,
  }));

  prependSessionObjects(cache, sessionId, enrichedFiles);
};

/**
 * On mutation: when CreateInference returns files, prepend them as session objects.
 */
const onCreateInference: UpdateResolver = (
  result: { createInference?: InferencePayload },
  _args: MutationCreateInferenceArgs,
  cache,
) => {
  if (result.createInference)
    prependInferenceFiles(cache, result.createInference);
};

/**
 * On subscription: when an inference reports progress with produced files,
 * prepend them as session objects.
 *
 * This is the resilient fallback for the `onSessionObjectEvent` CREATED events,
 * which are published the instant a forge starts and can be dropped if the
 * WebSocket subscription hasn't registered yet (Valkey pub/sub has no replay).
 * `onInferenceProgress` events carrying files fire later in the generation
 * lifecycle — after the subscription is reliably established — and repeat, so the
 * grid still populates when the early object events are missed. `prependSessionObjects`
 * dedups by id, so overlap with the other paths is a no-op.
 */
const onInferenceProgress: UpdateResolver = (
  result: { onInferenceProgress?: InferencePayload },
  _args: SubscriptionOnInferenceProgressArgs,
  cache,
) => {
  if (result.onInferenceProgress)
    prependInferenceFiles(cache, result.onInferenceProgress);
};

export const updatesConfig: Partial<UpdatesConfig> = {
  Mutation: {
    createInference: onCreateInference,
  },
  Subscription: {
    onSessionObjectEvent,
    onInferenceProgress,
  },
};
